import React, { useState, useEffect } from 'react';
import api from '../api';
import imageCompression from 'browser-image-compression';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  Autocomplete,
  Stack,
} from '@mui/material';
import { Save as SaveIcon, Calculate as CalculateIcon, PhotoCamera, Videocam as VideocamIcon } from '@mui/icons-material';

const TradeForm = ({ onAddTrade, onUpdateTrade, editingTrade, onCancelEdit }) => {
  const [formData, setFormData] = useState({
    tradeId: '',
    openDate: '',
    closeDate: '',
    symbol: 'NAS100',
    side: 'Buy',
    entry: '',
    exit: '',
    qty: '1.0',
    fee: '0',
    swap: '0',
    stopLoss: '',
    takeProfit: '',
    notes: '',
    tags: [],
    marketCondition: '',
    setupQuality: '',
    riskRewardRatio: '',
    actualRiskPercent: '',
    
    // Psychology state fields
    psychology: {
      mood: '',
      sleepHours: 0,
      stressLevel: 5, // Default to neutral
      confidenceLevel: 5, // Default to neutral
      focusLevel: 5, // Default to neutral
      psychologyNotes: '',
    },
  });
  
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [beforeImagePreview, setBeforeImagePreview] = useState(null);
  const [afterImagePreview, setAfterImagePreview] = useState(null);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  const [calculatedPnl, setCalculatedPnl] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const marketConditions = ['Trending', 'Ranging', 'Volatile', 'Calm', 'News-Driven'];
  const setupQualities = ['A+', 'A', 'B', 'C'];
  const commonTags = [
    'strong_fvg',
    'weak_fvg',
    'news_event',
    'london_session',
    'ny_session',
    'asian_session',
    'reversal',
    'continuation',
    'breakout',
    'retest',
    'liquidity_grab',
    'stop_hunt',
  ];

  // Effect to handle editing mode
  useEffect(() => {
    if (editingTrade) {
      setIsEditMode(true);
      setFormData({
        tradeId: editingTrade.tradeId || '',
        openDate: editingTrade.openDate ? new Date(editingTrade.openDate).toISOString().slice(0, 16) : '',
        closeDate: editingTrade.closeDate ? new Date(editingTrade.closeDate).toISOString().slice(0, 16) : '',
        symbol: editingTrade.symbol || 'NAS100',
        side: editingTrade.side || 'Buy',
        entry: editingTrade.entry?.toString() || '',
        exit: editingTrade.exit?.toString() || '',
        qty: editingTrade.qty?.toString() || '1.0',
        fee: editingTrade.fee?.toString() || '0',
        swap: editingTrade.swap?.toString() || '0',
        stopLoss: editingTrade.stopLoss?.toString() || '',
        takeProfit: editingTrade.takeProfit?.toString() || '',
        notes: editingTrade.notes || '',
        tags: editingTrade.tags || [],
        marketCondition: editingTrade.marketCondition || '',
        setupQuality: editingTrade.setupQuality || '',
        riskRewardRatio: editingTrade.riskRewardRatio?.toString() || '',
        actualRiskPercent: editingTrade.actualRiskPercent?.toString() || '',
        psychology: editingTrade.psychology || {
          mood: '',
          sleepHours: 0,
          stressLevel: 5,
          confidenceLevel: 5,
          focusLevel: 5,
          psychologyNotes: '',
        },
      });
      
      // Set calculated P&L if trade data exists
      if (editingTrade.pnl !== undefined) {
        setCalculatedPnl(editingTrade.pnl);
      }
      
      // Set image previews if they exist
      if (editingTrade.beforeImage) {
        setBeforeImage(editingTrade.beforeImage);
        setBeforeImagePreview(editingTrade.beforeImage);
      }
      if (editingTrade.afterImage) {
        setAfterImage(editingTrade.afterImage);
        setAfterImagePreview(editingTrade.afterImage);
      }
      if (editingTrade.video) {
        setVideo(editingTrade.video);
        setVideoPreview(editingTrade.video);
      }
    } else {
      setIsEditMode(false);
    }
  }, [editingTrade]);

  const handleBeforeImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();
        reader.onloadend = () => {
          setBeforeImage(reader.result);
          setBeforeImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
  };

  const handleAfterImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();
        reader.onloadend = () => {
          setAfterImage(reader.result);
          setAfterImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideo(reader.result);
        setVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Auto-calculate P&L when relevant fields change
    if (['entry', 'exit', 'qty', 'side', 'fee', 'swap'].includes(name)) {
      calculatePnl({ ...formData, [name]: value });
    }
  };

  const handlePsychologyChange = (field, value) => {
    setFormData({
      ...formData,
      psychology: {
        ...formData.psychology,
        [field]: value,
      },
    });
  };


  const calculatePnl = (data) => {
    const { entry, exit, qty, side, fee, swap } = data;
    if (entry && exit && qty) {
      const entryPrice = parseFloat(entry);
      const exitPrice = parseFloat(exit);
      const quantity = parseFloat(qty);
      const feeAmount = parseFloat(fee) || 0;
      const swapAmount = parseFloat(swap) || 0;

      let pnl;
      if (side === 'Buy') {
        pnl = (exitPrice - entryPrice) * quantity - feeAmount - swapAmount;
      } else {
        pnl = (entryPrice - exitPrice) * quantity - feeAmount - swapAmount;
      }

      setCalculatedPnl(pnl);
    }
  };

  const calculateRiskReward = () => {
    const { entry, stopLoss, takeProfit, side } = formData;
    if (entry && stopLoss && takeProfit) {
      const entryPrice = parseFloat(entry);
      const sl = parseFloat(stopLoss);
      const tp = parseFloat(takeProfit);

      let risk, reward;
      if (side === 'Buy') {
        risk = entryPrice - sl;
        reward = tp - entryPrice;
      } else {
        risk = sl - entryPrice;
        reward = entryPrice - tp;
      }

      const ratio = reward / risk;
      setFormData({
        ...formData,
        riskRewardRatio: ratio.toFixed(2),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Please login to add trades');
      return;
    }

    // Validate required fields
    if (!formData.tradeId || !formData.entry || !formData.exit || !formData.openDate || !formData.closeDate || !formData.symbol || !formData.side) {
      setError('Please fill in all required fields (Trade ID, Symbol, Side, Entry, Exit, Open Date, Close Date)');
      return;
    }

    // Prepare psychology data from form inputs
    const psychologyData = {
      ...formData.psychology,
      psychologyScore: 0, // This will be calculated by the backend
      riskLevel: 'LOW', // This will be calculated by the backend
    };
    
    const tradeData = {
      ...formData,
      psychology: psychologyData,
      pnl: calculatedPnl || 0,
      status: calculatedPnl >= 0 ? 'Win' : 'Loss',
      entry: parseFloat(formData.entry),
      exit: parseFloat(formData.exit),
      qty: parseFloat(formData.qty),
      fee: parseFloat(formData.fee) || 0,
      swap: parseFloat(formData.swap) || 0,
      stopLoss: formData.stopLoss ? parseFloat(formData.stopLoss) : null,
      takeProfit: formData.takeProfit ? parseFloat(formData.takeProfit) : null,
      riskRewardRatio: formData.riskRewardRatio ? parseFloat(formData.riskRewardRatio) : null,
      actualRiskPercent: formData.actualRiskPercent ? parseFloat(formData.actualRiskPercent) : null,
      beforeImage: beforeImage,
      afterImage: afterImage,
      video: video,
    };

    try {

      let response;
      if (isEditMode && editingTrade) {
        // Update existing trade
        response = await api.put(`/api/trades/${editingTrade._id}`, tradeData);
        onUpdateTrade(response.data);
        setSuccess(true);
        // Don't reset form in edit mode, just show success
        setTimeout(() => {
          setSuccess(false);
          if (onCancelEdit) onCancelEdit();
        }, 2000);
      } else {
        // Create new trade
        response = await api.post('/api/trades', tradeData);
        onAddTrade(response.data);
        setSuccess(true);
        
        // Reset form for new trade
        resetForm();
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Trade submission error:', err);
      console.error('Trade data being sent:', JSON.stringify(tradeData));
      console.error('Error response:', err.response?.data);
      
      // Show more detailed error message if available
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'add'} trade`;
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = `Validation errors: ${err.response.data.errors.join(', ')}`;
      } else if (err.response?.data?.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setError(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      tradeId: '',
      openDate: '',
      closeDate: '',
      symbol: 'NAS100',
      side: 'Buy',
      entry: '',
      exit: '',
      qty: '1.0',
      fee: '0',
      swap: '0',
      stopLoss: '',
      takeProfit: '',
      notes: '',
      tags: [],
      marketCondition: '',
      setupQuality: '',
      riskRewardRatio: '',
      actualRiskPercent: '',
      
      // Reset psychology state fields
      psychology: {
        mood: '',
        sleepHours: 0,
        stressLevel: 5, // Default to neutral
        confidenceLevel: 5, // Default to neutral
        focusLevel: 5, // Default to neutral
        psychologyNotes: '',
      },
    });
    setCalculatedPnl(null);
    setBeforeImage(null);
    setAfterImage(null);
    setBeforeImagePreview(null);
    setAfterImagePreview(null);
    setVideo(null);
    setVideoPreview(null);
  };

  const handleCancelEdit = () => {
    resetForm();
    setIsEditMode(false);
    if (onCancelEdit) onCancelEdit();
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Edit Trade' : 'Add New Trade'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Trade added successfully!
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Basic Trade Info */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Trade ID"
              name="tradeId"
              value={formData.tradeId}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Side</InputLabel>
              <Select
                name="side"
                value={formData.side}
                onChange={handleChange}
                label="Side"
              >
                <MenuItem value="Buy">Buy</MenuItem>
                <MenuItem value="Sell">Sell</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Quantity"
              name="qty"
              type="number"
              value={formData.qty}
              onChange={handleChange}
              required
              inputProps={{ step: '0.01' }}
            />
          </Grid>

          {/* Date Fields */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Open Date"
              name="openDate"
              type="datetime-local"
              value={formData.openDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Close Date"
              name="closeDate"
              type="datetime-local"
              value={formData.closeDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Price Fields */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Entry Price"
              name="entry"
              type="number"
              value={formData.entry}
              onChange={handleChange}
              required
              inputProps={{ step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Exit Price"
              name="exit"
              type="number"
              value={formData.exit}
              onChange={handleChange}
              required
              inputProps={{ step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Stop Loss"
              name="stopLoss"
              type="number"
              value={formData.stopLoss}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Take Profit"
              name="takeProfit"
              type="number"
              value={formData.takeProfit}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
            />
          </Grid>

          {/* Fees and Risk */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Fee"
              name="fee"
              type="number"
              value={formData.fee}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Swap"
              name="swap"
              type="number"
              value={formData.swap}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Risk/Reward Ratio"
              name="riskRewardRatio"
              type="number"
              value={formData.riskRewardRatio}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={calculateRiskReward}>
                      <CalculateIcon />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Actual Risk %"
              name="actualRiskPercent"
              type="number"
              value={formData.actualRiskPercent}
              onChange={handleChange}
              inputProps={{ step: '0.01' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>

          {/* Market Analysis */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Market Condition</InputLabel>
              <Select
                name="marketCondition"
                value={formData.marketCondition}
                onChange={handleChange}
                label="Market Condition"
              >
                <MenuItem value="">None</MenuItem>
                {marketConditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {condition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Setup Quality</InputLabel>
              <Select
                name="setupQuality"
                value={formData.setupQuality}
                onChange={handleChange}
                label="Setup Quality"
              >
                <MenuItem value="">None</MenuItem>
                {setupQualities.map((quality) => (
                  <MenuItem key={quality} value={quality}>
                    {quality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={commonTags}
              value={formData.tags}
              onChange={(event, newValue) => {
                setFormData({ ...formData, tags: newValue });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  helperText="Select or type custom tags"
                />
              )}
              freeSolo
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Trade Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Describe your setup, market conditions, emotions, lessons learned..."
            />
          </Grid>

          {/* Screenshot Uploaders */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" gutterBottom>
              Before Screenshot
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleBeforeImageChange}
                />
              </Button>
              {beforeImagePreview && (
                <Box
                  component="img"
                  sx={{
                    height: 100,
                    width: 150,
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                  }}
                  alt="Before trade"
                  src={beforeImagePreview}
                />
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" gutterBottom>
              After Screenshot
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleAfterImageChange}
                />
              </Button>
              {afterImagePreview && (
                <Box
                  component="img"
                  sx={{
                    height: 100,
                    width: 150,
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                  }}
                  alt="After trade"
                  src={afterImagePreview}
                />
              )}
            </Stack>
          </Grid>

          {/* Video Uploader */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Trade Video
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<VideocamIcon />}
              >
                Upload Video
                <input
                  type="file"
                  hidden
                  accept="video/*"
                  onChange={handleVideoChange}
                />
              </Button>
              {videoPreview && (
                <Box
                  component="video"
                  sx={{
                    height: 100,
                    width: 150,
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                  }}
                  controls
                  src={videoPreview}
                />
              )}
            </Stack>
         </Grid>
         
         {/* Psychology State Section */}
         <Grid item xs={12}>
           <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
             Psychology State
           </Typography>
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <FormControl fullWidth>
             <InputLabel>Mood</InputLabel>
             <Select
               name="mood"
               value={formData.psychology.mood}
               onChange={(e) => handlePsychologyChange('mood', e.target.value)}
               label="Mood"
             >
               <MenuItem value="">None</MenuItem>
               <MenuItem value="excellent">Excellent</MenuItem>
               <MenuItem value="good">Good</MenuItem>
               <MenuItem value="neutral">Neutral</MenuItem>
               <MenuItem value="poor">Poor</MenuItem>
               <MenuItem value="terrible">Terrible</MenuItem>
             </Select>
           </FormControl>
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <TextField
             fullWidth
             label="Sleep Hours"
             name="sleepHours"
             type="number"
             value={formData.psychology.sleepHours}
             onChange={(e) => handlePsychologyChange('sleepHours', parseFloat(e.target.value) || 0)}
             inputProps={{ step: '0.5', min: 0, max: 24 }}
           />
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <TextField
             fullWidth
             label="Stress Level (1-10)"
             name="stressLevel"
             type="number"
             value={formData.psychology.stressLevel}
             onChange={(e) => handlePsychologyChange('stressLevel', parseInt(e.target.value) || 0)}
             inputProps={{ min: 1, max: 10 }}
           />
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <TextField
             fullWidth
             label="Confidence Level (1-10)"
             name="confidenceLevel"
             type="number"
             value={formData.psychology.confidenceLevel}
             onChange={(e) => handlePsychologyChange('confidenceLevel', parseInt(e.target.value) || 0)}
             inputProps={{ min: 1, max: 10 }}
           />
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <TextField
             fullWidth
             label="Focus Level (1-10)"
             name="focusLevel"
             type="number"
             value={formData.psychology.focusLevel}
             onChange={(e) => handlePsychologyChange('focusLevel', parseInt(e.target.value) || 0)}
             inputProps={{ min: 1, max: 10 }}
           />
         </Grid>
         
         <Grid item xs={12}>
           <TextField
             fullWidth
             label="Psychology Notes"
             name="psychologyNotes"
             value={formData.psychology.psychologyNotes}
             onChange={(e) => handlePsychologyChange('psychologyNotes', e.target.value)}
             multiline
             rows={2}
             placeholder="Any additional notes about your mental state..."
           />
         </Grid>
         
         {/* Calculated P&L Display */}
         {calculatedPnl !== null && (
            <Grid item xs={12}>
              <Alert severity={calculatedPnl >= 0 ? 'success' : 'error'}>
                Calculated P&L: ${calculatedPnl.toFixed(2)}
              </Alert>
            </Grid>
          )}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                sx={{ flex: 1 }}
              >
                {isEditMode ? 'Update Trade' : 'Add Trade'}
              </Button>
              {isEditMode && (
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  onClick={handleCancelEdit}
                  sx={{ flex: 1 }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};


export default TradeForm;


